import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HeaderComponent } from '../../../shared/header/header.component';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { PendenciasService } from '../../../../services/pendencias/pendencias.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { UserService } from '../../../../services/user/user.service';
import { Pendencia, TipoPendencia } from '../../../../interfaces/pendencia';
import { BrandService } from '../../../../services/brand/brand.service';

@Component({
  selector: 'app-analise-pendencias',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink],
  templateUrl: './analise-pendencias.component.html',
})
export class AnalisePendenciasComponent implements OnInit {
  tabAtual: TipoPendencia = 'PROVISORIO';
  unidades: { id: number; name: string }[] = [];
  cdEstab = 0;
  dtInicial = '';
  dtFinal = '';
  filtroUsuario = '';

  dados: Record<TipoPendencia, Pendencia[]> = {
    PROVISORIO: [],
    SALDO_PENDENTE: [],
    SEM_NF_TITULO: [],
  };

  loading: Record<TipoPendencia, boolean> = {
    PROVISORIO: false,
    SALDO_PENDENTE: false,
    SEM_NF_TITULO: false,
  };

  vinculando = false;
  vinculandoConta: number | null = null;
  mensagem: string | null = null;
  mensagemTipo: 'ok' | 'err' | 'info' = 'info';

  private username = '';

  constructor(
    private router: Router,
    private cashRegisterService: CashRegisterService,
    private pendenciasService: PendenciasService,
    private userService: UserService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit() {
    this.username = this.localStorage.get('login') as string;
    if (!this.username) {
      this.router.navigate(['/home']);
      return;
    }

    const profileLocal = this.localStorage.get('profile') as string | null;
    if (profileLocal !== 'Administrador') {
      const perfil = await this.cashRegisterService.getUserProfile(this.username);
      if (!perfil?.isSupervisorGlobal && !perfil?.isSupervisorUnit) {
        this.router.navigate(['/lancamentos-financeiros']);
        return;
      }
    }

    const hoje = new Date();
    const trinta = new Date();
    trinta.setDate(hoje.getDate() - 30);
    this.dtInicial = this.toISO(trinta);
    this.dtFinal = this.toISO(hoje);

    try {
      const resp = await this.userService.getUnits(this.username, this.brandService.cdEmpresa);
      this.unidades = (resp?.data || []) as { id: number; name: string }[];
    } catch {
      this.unidades = [];
    }

    await this.carregarTodas();
  }

  async carregarTodas() {
    await Promise.all([
      this.carregarTab('PROVISORIO'),
      this.carregarTab('SALDO_PENDENTE'),
      this.carregarTab('SEM_NF_TITULO'),
    ]);
  }

  trocarTab(tipo: TipoPendencia) {
    this.tabAtual = tipo;
  }

  async carregarTab(tipo: TipoPendencia) {
    if (!this.periodoValido()) {
      this.toast('Período máximo permitido é de 31 dias.', 'err');
      return;
    }
    this.loading[tipo] = true;
    this.mensagem = null;
    try {
      this.dados[tipo] = await this.pendenciasService.getPendencias(
        tipo,
        this.cdEstab,
        this.dtInicial,
        this.dtFinal,
        this.username,
      );
    } catch (e: any) {
      this.toast(e?.error?.feedback || 'Erro ao buscar pendências.', 'err');
    } finally {
      this.loading[tipo] = false;
    }
  }

  periodoValido(): boolean {
    if (!this.dtInicial || !this.dtFinal) return false;
    const ini = new Date(this.dtInicial);
    const fim = new Date(this.dtFinal);
    if (fim < ini) return false;
    const diff = Math.floor((fim.getTime() - ini.getTime()) / (1000 * 60 * 60 * 24));
    return diff <= 31;
  }

  async atualizarTudo() {
    this.dados = { PROVISORIO: [], SALDO_PENDENTE: [], SEM_NF_TITULO: [] };
    await this.carregarTodas();
  }

  async vincularConta(p: Pendencia) {
    this.vinculandoConta = p.nrInternoConta;
    try {
      const r = await this.pendenciasService.vincularSaldos(this.username, p.nrInternoConta);
      if (r.processados > 0) {
        this.toast(`#${p.nrInternoConta} — ${r.processados} vínculos${r.erros > 0 ? `, ${r.erros} erros` : ''}.`, 'ok');
      } else {
        this.toast(`#${p.nrInternoConta} — nenhum saldo disponível para vincular.`, 'info');
      }
      await this.carregarTodas();
    } catch (e: any) {
      this.toast(e?.error?.feedback || 'Erro ao vincular saldo.', 'err');
    } finally {
      this.vinculandoConta = null;
    }
  }

  async vincularTodos() {
    this.vinculando = true;
    try {
      const r = await this.pendenciasService.vincularSaldos(this.username);
      this.toast(`Concluído: ${r.processados} vínculos${r.erros > 0 ? `, ${r.erros} erros` : ''}.`, 'ok');
      await this.atualizarTudo();
    } catch (e: any) {
      this.toast(e?.error?.feedback || 'Erro ao vincular saldos.', 'err');
    } finally {
      this.vinculando = false;
    }
  }

  pendenciasFiltradas(tipo: TipoPendencia): Pendencia[] {
    const base = this.dados[tipo];
    if (!this.filtroUsuario) return base;
    const t = this.filtroUsuario.toLowerCase();
    return base.filter(p => (p.usuario || '').toLowerCase().includes(t));
  }

  badge(tipo: TipoPendencia): number {
    return this.dados[tipo].length;
  }

  formatCurrency(v: number | null | undefined): string {
    return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  formatDate(v: string | null): string {
    if (!v) return '—';
    return new Date(v).toLocaleDateString('pt-BR');
  }

  voltar() {
    this.router.navigate(['/lancamentos-financeiros']);
  }

  private toISO(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private toast(msg: string, tipo: 'ok' | 'err' | 'info' = 'info') {
    this.mensagem = msg;
    this.mensagemTipo = tipo;
    setTimeout(() => { if (this.mensagem === msg) this.mensagem = null; }, 5000);
  }
}
